import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Workflow,
  Plus,
  Pencil,
  Trash2,
  X,
  Bot,
  Shield,
} from 'lucide-react';
import { cn } from '@/lib/cn';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ChainSummary {
  id: string;
  name: string;
  description: string | null;
  triggerEvent: string;
  enabled: boolean;
  createdAt: string;
}

interface AgentDefinitionSummary {
  agentKey: string;
  name: string;
  description: string;
  scope: string;
  category: string;
  runnerType: string;
  defaultConfidenceThreshold: number;
  icon: string | null;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const TRIGGER_EVENTS = [
  'LEAD_CREATED',
  'STAGE_CHANGED',
  'CLOSED_WON',
  'STALE_OPPORTUNITY',
  'MANUAL',
  'FIELD_CHANGED',
  'TASK_COMPLETED',
] as const;

const CATEGORIES = [
  'PROSPECTING',
  'SCORING',
  'INTELLIGENCE',
  'COMMUNICATION',
  'ONBOARDING',
  'RENEWAL',
  'REPORTING',
  'CUSTOM',
] as const;

const RUNNER_TYPES = ['LLM', 'RULE', 'HYBRID', 'EXTERNAL'] as const;

const TRIGGER_COLORS: Record<string, string> = {
  LEAD_CREATED: 'bg-emerald-900/40 text-emerald-200',
  STAGE_CHANGED: 'bg-blue-900/40 text-blue-200',
  CLOSED_WON: 'bg-purple-900/40 text-purple-200',
  STALE_OPPORTUNITY: 'bg-amber-900/40 text-amber-200',
  MANUAL: 'bg-muted text-foreground',
  FIELD_CHANGED: 'bg-cyan-900/40 text-cyan-200',
  TASK_COMPLETED: 'bg-indigo-900/40 text-indigo-200',
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function ChainsPage() {
  const [tab, setTab] = useState<'chains' | 'agents'>('chains');
  const [chains, setChains] = useState<ChainSummary[]>([]);
  const [agents, setAgents] = useState<AgentDefinitionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);

  // Create-chain dialog
  const [showChainForm, setShowChainForm] = useState(false);
  const [chainForm, setChainForm] = useState({
    name: '',
    description: '',
    triggerEvent: TRIGGER_EVENTS[0] as string,
  });

  // Create-agent dialog
  const [showAgentForm, setShowAgentForm] = useState(false);
  const [agentForm, setAgentForm] = useState({
    agentKey: '',
    name: '',
    description: '',
    category: CATEGORIES[0] as string,
    runnerType: RUNNER_TYPES[0] as string,
    promptTemplate: '',
    defaultConfidenceThreshold: 70,
  });

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<{
    type: 'chain' | 'agent';
    id: string;
    name: string;
  } | null>(null);

  function flash(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [chainsRes, agentsRes] = await Promise.all([
        apiClient.get<ChainSummary[]>('/agent-chains'),
        apiClient.get<AgentDefinitionSummary[]>('/agent-definitions'),
      ]);
      setChains(chainsRes.data);
      setAgents(agentsRes.data);
    } catch {
      // keep empty
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* ------ Chain CRUD ------ */

  async function toggleChain(id: string, enabled: boolean) {
    try {
      await apiClient.patch(`/agent-chains/${id}`, { enabled });
      setChains((prev) =>
        prev.map((c) => (c.id === id ? { ...c, enabled } : c)),
      );
      flash(`Chain ${enabled ? 'enabled' : 'disabled'}`);
    } catch {
      flash('Failed to update chain');
    }
  }

  async function createChain() {
    if (!chainForm.name.trim()) return;
    try {
      await apiClient.post('/agent-chains', {
        name: chainForm.name,
        description: chainForm.description || null,
        triggerEvent: chainForm.triggerEvent,
        enabled: false,
        triggerConditions: [],
        steps: [],
      });
      setShowChainForm(false);
      setChainForm({ name: '', description: '', triggerEvent: TRIGGER_EVENTS[0] });
      flash('Chain created');
      load();
    } catch {
      flash('Failed to create chain');
    }
  }

  async function deleteChain(id: string) {
    try {
      await apiClient.delete(`/agent-chains/${id}`);
      setDeleteTarget(null);
      flash('Chain deleted');
      load();
    } catch {
      flash('Failed to delete chain');
    }
  }

  /* ------ Agent definition CRUD ------ */

  async function createAgent() {
    if (!agentForm.agentKey.trim() || !agentForm.name.trim()) return;
    try {
      await apiClient.post('/agent-definitions', {
        agentKey: agentForm.agentKey,
        name: agentForm.name,
        description: agentForm.description,
        scope: 'TENANT',
        category: agentForm.category,
        runnerType: agentForm.runnerType,
        promptTemplate: agentForm.promptTemplate || null,
        defaultConfidenceThreshold: agentForm.defaultConfidenceThreshold,
      });
      setShowAgentForm(false);
      setAgentForm({
        agentKey: '',
        name: '',
        description: '',
        category: CATEGORIES[0],
        runnerType: RUNNER_TYPES[0],
        promptTemplate: '',
        defaultConfidenceThreshold: 70,
      });
      flash('Agent definition created');
      load();
    } catch {
      flash('Failed to create agent definition');
    }
  }

  async function deleteAgent(key: string) {
    try {
      await apiClient.delete(`/agent-definitions/${key}`);
      setDeleteTarget(null);
      flash('Agent definition deleted');
      load();
    } catch {
      flash('Failed to delete agent definition');
    }
  }

  /* ------ Render ------ */

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className="fixed right-6 top-20 z-50 rounded-lg border bg-card px-4 py-2 text-sm shadow-lg">
          {toast}
        </div>
      )}

      {/* Delete confirmation dialog */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-xl border bg-card p-6 shadow-xl">
            <h3 className="text-lg font-semibold">Delete {deleteTarget.type}?</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This
              action cannot be undone.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  if (deleteTarget.type === 'chain') deleteChain(deleteTarget.id);
                  else deleteAgent(deleteTarget.id);
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Agent Chains</h1>
          <p className="text-sm text-muted-foreground">
            Orchestrate multi-step agent workflows triggered by pipeline events.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        <button
          onClick={() => setTab('chains')}
          className={cn(
            'px-4 py-2 text-sm font-medium transition-colors',
            tab === 'chains'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <Workflow className="mr-1.5 inline-block h-4 w-4" />
          Chains
        </button>
        <button
          onClick={() => setTab('agents')}
          className={cn(
            'px-4 py-2 text-sm font-medium transition-colors',
            tab === 'agents'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <Bot className="mr-1.5 inline-block h-4 w-4" />
          Agent Definitions
        </button>
      </div>

      {loading && (
        <p className="text-sm text-muted-foreground">Loading...</p>
      )}

      {/* ===== Chains tab ===== */}
      {!loading && tab === 'chains' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowChainForm(true)}>
              <Plus className="mr-1 h-4 w-4" />
              Chain
            </Button>
          </div>

          {/* Create chain form */}
          {showChainForm && (
            <div className="rounded-xl border bg-card p-5">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-semibold">New Chain</h3>
                <button onClick={() => setShowChainForm(false)}>
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium">Name</label>
                  <Input
                    value={chainForm.name}
                    onChange={(e) =>
                      setChainForm((f) => ({ ...f, name: e.target.value }))
                    }
                    placeholder="e.g. Lead Enrichment Pipeline"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium">Trigger Event</label>
                  <select
                    value={chainForm.triggerEvent}
                    onChange={(e) =>
                      setChainForm((f) => ({ ...f, triggerEvent: e.target.value }))
                    }
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    {TRIGGER_EVENTS.map((ev) => (
                      <option key={ev} value={ev}>
                        {ev.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-xs font-medium">Description</label>
                  <Input
                    value={chainForm.description}
                    onChange={(e) =>
                      setChainForm((f) => ({ ...f, description: e.target.value }))
                    }
                    placeholder="Optional description"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowChainForm(false)}>
                  Cancel
                </Button>
                <Button onClick={createChain}>Create Chain</Button>
              </div>
            </div>
          )}

          {/* Chain list */}
          {chains.length === 0 && !showChainForm && (
            <div className="rounded-xl border bg-card px-6 py-12 text-center">
              <Workflow className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">
                No chains configured yet. Create one to get started.
              </p>
            </div>
          )}

          <div className="space-y-2">
            {chains.map((chain) => (
              <div
                key={chain.id}
                className="flex items-center justify-between rounded-xl border bg-card px-5 py-4"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium">{chain.name}</h3>
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-[10px] font-medium',
                        TRIGGER_COLORS[chain.triggerEvent] ?? 'bg-muted text-foreground',
                      )}
                    >
                      {chain.triggerEvent.replace(/_/g, ' ')}
                    </span>
                  </div>
                  {chain.description && (
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      {chain.description}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  {/* Enabled toggle */}
                  <button
                    onClick={() => toggleChain(chain.id, !chain.enabled)}
                    className={cn(
                      'relative h-6 w-11 rounded-full transition-colors',
                      chain.enabled ? 'bg-primary' : 'bg-muted',
                    )}
                    title={chain.enabled ? 'Disable' : 'Enable'}
                  >
                    <span
                      className={cn(
                        'absolute top-0.5 h-5 w-5 rounded-full bg-card shadow transition-transform',
                        chain.enabled ? 'translate-x-5' : 'translate-x-0.5',
                      )}
                    />
                  </button>

                  <Link to={`/admin/chains/${chain.id}/edit`}>
                    <Button variant="outline" size="sm">
                      <Pencil className="mr-1 h-3.5 w-3.5" />
                      Edit
                    </Button>
                  </Link>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      setDeleteTarget({
                        type: 'chain',
                        id: chain.id,
                        name: chain.name,
                      })
                    }
                  >
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== Agent Definitions tab ===== */}
      {!loading && tab === 'agents' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowAgentForm(true)}>
              <Plus className="mr-1 h-4 w-4" />
              Custom Agent
            </Button>
          </div>

          {/* Create agent form */}
          {showAgentForm && (
            <div className="rounded-xl border bg-card p-5">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-semibold">New Custom Agent</h3>
                <button onClick={() => setShowAgentForm(false)}>
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium">Agent Key</label>
                  <Input
                    value={agentForm.agentKey}
                    onChange={(e) =>
                      setAgentForm((f) => ({ ...f, agentKey: e.target.value }))
                    }
                    placeholder="e.g. CUSTOM_ENRICHMENT"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium">Name</label>
                  <Input
                    value={agentForm.name}
                    onChange={(e) =>
                      setAgentForm((f) => ({ ...f, name: e.target.value }))
                    }
                    placeholder="e.g. Lead Enrichment Agent"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium">Category</label>
                  <select
                    value={agentForm.category}
                    onChange={(e) =>
                      setAgentForm((f) => ({ ...f, category: e.target.value }))
                    }
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium">Runner Type</label>
                  <select
                    value={agentForm.runnerType}
                    onChange={(e) =>
                      setAgentForm((f) => ({ ...f, runnerType: e.target.value }))
                    }
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    {RUNNER_TYPES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-xs font-medium">Description</label>
                  <Input
                    value={agentForm.description}
                    onChange={(e) =>
                      setAgentForm((f) => ({ ...f, description: e.target.value }))
                    }
                    placeholder="What does this agent do?"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-xs font-medium">
                    Prompt Template (for LLM runners)
                  </label>
                  <textarea
                    value={agentForm.promptTemplate}
                    onChange={(e) =>
                      setAgentForm((f) => ({ ...f, promptTemplate: e.target.value }))
                    }
                    rows={4}
                    placeholder="Enter the LLM prompt template..."
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium">
                    Default Confidence Threshold: {agentForm.defaultConfidenceThreshold}%
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={agentForm.defaultConfidenceThreshold}
                    onChange={(e) =>
                      setAgentForm((f) => ({
                        ...f,
                        defaultConfidenceThreshold: parseInt(e.target.value),
                      }))
                    }
                    className="w-full accent-primary"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAgentForm(false)}>
                  Cancel
                </Button>
                <Button onClick={createAgent}>Create Agent</Button>
              </div>
            </div>
          )}

          {/* Agent definition cards */}
          {agents.length === 0 && !showAgentForm && (
            <div className="rounded-xl border bg-card px-6 py-12 text-center">
              <Bot className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">
                No agent definitions found.
              </p>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {agents.map((agent) => {
              const isBuiltin = agent.scope === 'BUILTIN';
              return (
                <div
                  key={agent.agentKey}
                  className="rounded-xl border bg-card p-5"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Bot className="h-4 w-4" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold">{agent.name}</h3>
                        <span className="text-[10px] font-mono text-muted-foreground">
                          {agent.agentKey}
                        </span>
                      </div>
                    </div>
                    {isBuiltin && (
                      <span className="flex items-center gap-1 rounded-full bg-blue-900/40 px-2 py-0.5 text-[10px] font-medium text-blue-200">
                        <Shield className="h-3 w-3" />
                        Built-in
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {agent.description}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-foreground">
                      {agent.category}
                    </span>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-foreground">
                      {agent.runnerType}
                    </span>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-foreground">
                      {agent.defaultConfidenceThreshold}% threshold
                    </span>
                  </div>
                  {!isBuiltin && (
                    <div className="mt-3 flex gap-2">
                      <Button variant="outline" size="sm">
                        <Pencil className="mr-1 h-3 w-3" />
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setDeleteTarget({
                            type: 'agent',
                            id: agent.agentKey,
                            name: agent.name,
                          })
                        }
                      >
                        <Trash2 className="mr-1 h-3 w-3" />
                        Delete
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
